// Pricing migration 32 (ADO-3985), T9: the shared multi-buy deal lookup.
//
// One Zustand store (ADR-004) every product surface reads its deals from, keyed per SKU. Pages
// ask for the deals of the products they show through `useMultibuyDeals`, once per page beside
// `usePrices`; cards read their own SKU's entry.
//
// Mirrors the price store (`pricing/usePriceStore.ts`): same-tick batching on a microtask,
// in-flight coalescing, and a generation bumped whenever the key (`priceKey`: the ordering entry
// and the trading account) changes, so an answer for a key we left is never written. No TTL and
// no persistence. Deals are shown only with `VITE_REAL_PRICES_ENABLED` on (D-5); with it off, or
// with no ordering entry, nothing is requested.
//
// A SKU whose read failed is asked again the next time a page asks for it. `invalidate` is D-2's
// re-ask: after a basket write that touches a deal's products, the deal's
// progress changed server-side, so the screen asks again for those SKUs. The old answer stays on
// screen until the new one lands, and a read already on the wire for them is not ridden (its
// answer predates the write); the per-SKU write stamp keeps that older answer from overwriting
// the newer one.

import { useEffect } from 'react';
import { create } from 'zustand';
import { fetchMultibuyDeals, type MultibuyDeal } from '../api/multibuy-deals.client';
import { useSessionStore } from '../auth/useSessionStore';
import { realPricesEnabled } from '../pricing/realPrices';
import {
  priceKey,
  useCurrentPriceKey,
  usePriceAccountCode,
  type ReadOutcome,
} from '../pricing/usePriceStore';

/** One SKU's deal state. A SKU with no entry has not been asked for yet, and shows no deal. */
export type MultibuyDealsEntry =
  | { readonly state: 'loading' }
  /** Answered: every live deal reaching the product, none being an empty list. */
  | { readonly state: 'ready'; readonly deals: readonly MultibuyDeal[] }
  /** Nobody answered: the request failed, or the answer for this SKU was unusable. */
  | { readonly state: 'failed' };

interface BatchOutcome {
  readonly unread: ReadonlySet<string>;
  readonly superseded: ReadonlySet<string>;
}

interface MultibuyDealsStoreState {
  /** The entry and account the entries belong to (`priceKey`), or `null` before any load. */
  key: string | null;
  entries: Readonly<Record<string, MultibuyDealsEntry>>;
  /** Load the deals of `skuCodes` under `entryKey` for `accountCode`; resolves to this caller's
   *  `ReadOutcome` once the read settles. */
  loadForSkus: (
    entryKey: string,
    accountCode: string | null,
    skuCodes: readonly string[],
  ) => Promise<ReadOutcome>;
  /** Ask again for the SKUs among `skuCodes` the store holds, under the key it holds (D-2). A
   *  no-op (`'answered'`) for SKUs it does not hold, before any load and with the flag off. */
  invalidate: (skuCodes: readonly string[]) => Promise<ReadOutcome>;
  /** Drop everything (tests, session hygiene). */
  reset: () => void;
}

/** Bumped on every key change and reset: a batch writes only for the generation it was opened
 *  in. */
let generation = 0;
let writeStampCounter = 0;
/** Per-SKU stamp of the write that produced the current answer: an answer from a batch opened
 *  before the one that last wrote the SKU is dropped (an `invalidate` overtaking a read). */
let writeStamps: Record<string, number> = {};
/** SKU to the promise of the batch currently fetching it, for the current generation. */
let inFlightReads: Record<string, Promise<BatchOutcome>> = {};
/** The entry and account of the current key, for `invalidate`. */
let currentRequest: { readonly entryKey: string; readonly accountCode: string | null } | null =
  null;

interface PendingBatch {
  readonly entryKey: string;
  readonly accountCode: string | null;
  readonly generation: number;
  readonly skus: Set<string>;
  readonly promise: Promise<BatchOutcome>;
  readonly settle: (outcome: BatchOutcome) => void;
  readonly startStamp: number;
}
let pendingBatch: PendingBatch | null = null;

function dropEverything(): void {
  generation += 1;
  writeStamps = {};
  inFlightReads = {};
  pendingBatch = null;
}

export const useMultibuyDealsStore = create<MultibuyDealsStoreState>((set, get) => {
  /** Send one accumulated batch. Always settles its promise and clears its in-flight entries. */
  async function flushBatch(batch: PendingBatch): Promise<void> {
    if (pendingBatch === batch) pendingBatch = null;
    const toFetch = [...batch.skus];
    let unread: ReadonlySet<string> = new Set(toFetch);
    let superseded: ReadonlySet<string> = new Set<string>();
    try {
      if (batch.generation !== generation) {
        superseded = new Set(toFetch);
        unread = new Set<string>();
        return;
      }
      const { answers, unreadSkus } = await fetchMultibuyDeals(
        batch.entryKey,
        batch.accountCode,
        toFetch,
      );
      if (batch.generation !== generation) {
        superseded = new Set(toFetch);
        unread = new Set<string>();
        return;
      }
      unread = new Set(unreadSkus);
      const next: Record<string, MultibuyDealsEntry> = { ...get().entries };
      const write = (sku: string, entry: MultibuyDealsEntry): void => {
        if ((writeStamps[sku] ?? 0) > batch.startStamp) return;
        writeStamps[sku] = batch.startStamp;
        next[sku] = entry;
      };
      for (const answer of answers) write(answer.skuCode, { state: 'ready', deals: answer.deals });
      for (const sku of unreadSkus) write(sku, { state: 'failed' });
      set({ entries: next });
    } catch {
      // `fetchMultibuyDeals` never rejects, so reaching here means something unexpected did.
      if (batch.generation === generation) {
        const next: Record<string, MultibuyDealsEntry> = { ...get().entries };
        for (const sku of toFetch) {
          if (next[sku]?.state === 'loading') next[sku] = { state: 'failed' };
        }
        set({ entries: next });
      }
      unread = new Set(toFetch);
      superseded = new Set<string>();
    } finally {
      for (const sku of toFetch) {
        if (inFlightReads[sku] === batch.promise) delete inFlightReads[sku];
      }
      batch.settle({ unread, superseded });
    }
  }

  /** Add `skus` to the tick's open batch (opening one if needed) and return its promise. */
  function enqueueRead(
    entryKey: string,
    accountCode: string | null,
    skus: readonly string[],
  ): Promise<BatchOutcome> {
    if (pendingBatch === null || pendingBatch.generation !== generation) {
      let settle!: (outcome: BatchOutcome) => void;
      const promise = new Promise<BatchOutcome>((resolve) => {
        settle = resolve;
      });
      const batch: PendingBatch = {
        entryKey,
        accountCode,
        generation,
        skus: new Set(),
        promise,
        settle,
        startStamp: ++writeStampCounter,
      };
      pendingBatch = batch;
      queueMicrotask(() => void flushBatch(batch));
    }
    const batch = pendingBatch;
    for (const sku of skus) {
      batch.skus.add(sku);
      inFlightReads[sku] = batch.promise;
    }
    return batch.promise;
  }

  /** The caller's outcome over the batches its SKUs rode. */
  async function outcomeOf(
    skus: readonly string[],
    riding: ReadonlySet<Promise<BatchOutcome>>,
  ): Promise<ReadOutcome> {
    const outcomes = await Promise.all([...riding]);
    if (skus.some((sku) => outcomes.some((outcome) => outcome.unread.has(sku)))) return 'unread';
    if (skus.some((sku) => outcomes.some((outcome) => outcome.superseded.has(sku))))
      return 'superseded';
    return 'answered';
  }

  return {
    key: null,
    entries: {},

    async loadForSkus(entryKey, accountCode, skuCodes) {
      if (!realPricesEnabled()) return 'answered'; // D-5: the flag is off, never ask
      const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
      if (unique.length === 0) return 'answered';

      const key = priceKey(entryKey, accountCode);
      let entries = get().entries;
      if (get().key !== key) {
        dropEverything();
        entries = {};
      }
      currentRequest = { entryKey, accountCode };
      // A SKU whose read failed is asked again (T13): it shows no deal until an answer lands.
      const loading = unique.filter(
        (sku) =>
          entries[sku] === undefined ||
          (entries[sku]?.state === 'failed' && inFlightReads[sku] === undefined),
      );
      if (loading.length > 0 || get().key !== key) {
        const next: Record<string, MultibuyDealsEntry> = { ...entries };
        for (const sku of loading) next[sku] = { state: 'loading' };
        set({ key, entries: next });
      }

      const riding = new Set<Promise<BatchOutcome>>();
      const toFetch: string[] = [];
      for (const sku of unique) {
        const existing = inFlightReads[sku];
        if (existing !== undefined) riding.add(existing);
        else toFetch.push(sku);
      }
      if (toFetch.length > 0) riding.add(enqueueRead(entryKey, accountCode, toFetch));
      return outcomeOf(unique, riding);
    },

    async invalidate(skuCodes) {
      if (!realPricesEnabled() || currentRequest === null) return 'answered';
      const { entries } = get();
      const held = [...new Set(skuCodes)].filter((sku) => entries[sku] !== undefined);
      if (held.length === 0) return 'answered';
      // A read already on the wire answers for the basket as it was before the write: do not
      // ride it. Its answer cannot overwrite this one (the write stamp).
      for (const sku of held) {
        if (pendingBatch === null || !pendingBatch.skus.has(sku)) delete inFlightReads[sku];
      }
      const toFetch = held.filter((sku) => inFlightReads[sku] === undefined);
      const riding = new Set<Promise<BatchOutcome>>(
        held.flatMap((sku) => inFlightReads[sku] ?? []),
      );
      if (toFetch.length > 0) {
        riding.add(enqueueRead(currentRequest.entryKey, currentRequest.accountCode, toFetch));
      }
      return outcomeOf(held, riding);
    },

    reset() {
      dropEverything();
      currentRequest = null;
      set({ key: null, entries: {} });
    },
  };
});

/**
 * Ask for the deals of the products a page or list shows, in one request. Call it once per page,
 * beside `usePrices`, with the same SKUs. Re-asks when the SKU set, the ordering entry or the
 * trading account changes. A no-op with the flag off or with no ordering entry chosen.
 */
export function useMultibuyDeals(skuCodes: readonly string[]): void {
  // Keyed on the VALUE of the SKU list: pages rebuild the array on every render.
  const skuKey = [...new Set(skuCodes)].sort().join(',');
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const accountCode = usePriceAccountCode();

  useEffect(() => {
    if (!realPricesEnabled() || entryKey === null || skuKey === '') return;
    void useMultibuyDealsStore.getState().loadForSkus(entryKey, accountCode, skuKey.split(','));
  }, [skuKey, entryKey, accountCode]);
}

/** The deal state of one SKU for the session's current entry and account; `undefined` (no deal)
 *  when the store holds another key's entries or none for this SKU. */
export function useMultibuyDealsEntry(sku: string): MultibuyDealsEntry | undefined {
  const currentKey = useCurrentPriceKey();
  return useMultibuyDealsStore((s) =>
    currentKey !== null && s.key === currentKey ? s.entries[sku] : undefined,
  );
}
